<script setup>
import {computed, ref, defineProps, defineEmits} from "vue";
import {useThemesStore} from "@/store/NewsFeed/themes";
import {useRubricsStore} from "@/store/NewsFeed/rubrics";
import {useNewsFeedStore} from "@/store/NewsFeed/newsFeed";
import NewsPageStoryNatureSelector
  from "@/components/NewsPage/NewsPageRoof/NewsPageStory/NewsPageStoryNatureSelector.vue";
import NewsPageStoryDropdown
  from "@/components/NewsPage/NewsPageRoof/NewsPageStory/NewsPageStoryDropdown.vue";
import { t } from "@/controllerLK/AvalLocalization";

defineProps({
  isOpen: Boolean
});

// stores
const themesStore = useThemesStore();
const rubricsStore = useRubricsStore();
const newsFeedStore = useNewsFeedStore();

const emit = defineEmits(["close"]);

// Темы.
// Активная выбранная тема проекта.
const selectedProjectTheme = computed(() => themesStore.chosenThemes);
// Активная выбранная тема сюжета.
const selectedStoryTheme = ref(selectedProjectTheme.value);
// Список всех тем проекта.
const projectThemesList = computed(() => themesStore.allThemes);

// Рубрики.
// Активные выбранные рубрики проекта.
const selectedProjectRubric = computed(() => rubricsStore.chosenRubrics);
// Активные выбранные рубрики сюжета.
const selectedStoryRubric = ref(selectedProjectRubric.value);
// Список всех рубрик проекта НА МОНИТОРИНГЕ.
const projectRubricList = computed(() => rubricsStore.allRubrics.filter((rubric) => rubric.is_tracked));

// Поисковый запрос.
// Текущение значение поисковой строки проекта
const searchQueryProject = computed(() => newsFeedStore.searchValue);
// Текущение значение поисковой строки сюжета.
const searchQueryStory = ref(searchQueryProject.value);
</script>

<template>
  <Teleport to="#app">
    <div class="modal-overlay">
      <div
        class="modal-overlay__backdrop"
        @click="emit('close')"
      />

      <aside class="story-modal">
        <header class="story-modal__header">
          <h2 class="story-modal__title">
            {{ t("news_page_story.title") }}
          </h2>
          <button
            class="story-modal__close-btn"
            type="button"
            aria-label="Закрыть"
            @click="emit('close')"
          >
            <AvalIconCross />
          </button>
        </header>

        <div class="story-modal__body">
          <section class="story-modal__section">
            <header class="story-modal__section-header">
              <h3 class="story-modal__section-title required-symbol">
                {{ t("news_page_story.query_section.label") }}
              </h3>
              <p class="story-modal__section-description">
                {{ t("news_page_story.query_section.description") }}
              </p>
            </header>
            <!-- Выбор темы и рубрик сюжета -->
            <div class="story-modal__fields-row">
              <NewsPageStoryDropdown
                v-model="selectedStoryTheme"
                :label="t('news_page_story.dropdown.theme_label')"
                :items="projectThemesList"
                :all-count="selectedProjectTheme.length"
              />
              <NewsPageStoryDropdown
                v-model="selectedStoryRubric"
                :label="t('news_page_story.dropdown.rubric_label')"
                :items="projectRubricList"
                :all-count="selectedProjectRubric.length"
              />
            </div>

            <div class="story-modal__field">
              <AvalInputSearchSecondary
                v-model="searchQueryStory"
                :placeholder="t('news_page_story.searchbar.placeholder')"
                size="large"
              />
            </div>
          </section>

          <section class="story-modal__section">
            <header class="story-modal__section-header">
              <h3 class="story-modal__section-title">
                {{ t("news_page_story.nature_section.label") }}
              </h3>
              <p class="story-modal__section-description">
                {{ t("news_page_story.nature_section.description") }}
              </p>
            </header>
            <NewsPageStoryNatureSelector />
          </section>
        </div>
        <footer class="story-modal__footer">
          <AvalButtonAccentPrimary
            class="story-modal__accept-btn"
            size="large"
            :disabled="true"
            @click="emit('close')"
          >
            {{ t("news_page_story.button.create") }}
          </AvalButtonAccentPrimary>
          <AvalButtonNeutralSecondary
            class="story-modal__decline-btn"
            size="large"
            @click="emit('close')"
          >
            {{ t("news_page_story.button.cancel") }}
          </AvalButtonNeutralSecondary>
        </footer>
      </aside>
    </div>
  </Teleport>
</template>

<style scoped lang="scss">
.modal-overlay {
  position: fixed;
  z-index: 999;
  inset: 0;

  &__backdrop {
    position: absolute;
    inset: 0;
    background-color: rgba(0, 0, 0, 0.5);
  }
}

.story-modal {
  position: absolute;
  top: 0;
  right: 0;
  display: flex;
  flex-direction: column;
  width: 600px;
  height: 100%;
  padding: 20px;
  background-color: var(--background-bg-white);
  box-shadow: 0 4px 20px 0 rgba(0, 0, 0, 0.15);
  gap: 24px;

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
  }

  &__title {
    color: var(--text-text-black);
    font-weight: 500;
    font-size: 20px;
    line-height: 28px;
  }

  &__close-btn {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 0;
    border: none;
    background: none;
    cursor: pointer;
    width: 24px;
    height: 24px;
    color: var(--icon-icon-secondary);
    transition: color 0.2s;

    &:hover {
      color: var(--icon-icon-on);
    }

    svg {
      width: 20px;
      height: 20px;
    }
  }

  &__body {
    display: flex;
    flex-direction: column;
    gap: 20px;
  }

  &__section {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  &__section-header {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  &__section-title {
    font-weight: 500;
    font-size: 14px;
    line-height: 18px;
    color: var(--text-text-black);
  }

  &__section-description {
    margin: 0;
    color: var(--text-text-gray);
    font-weight: 400;
    font-size: 12px;
    line-height: 1.4;
  }

  &__fields-row {
    display: flex;
    gap: 10px;
  }

  &__field {
    display: flex;
    flex-direction: column;
    flex: 1;
  }

  &__footer {
    display: flex;
    gap: 8px;
    margin-top: auto;
  }

  &__accept-btn, &__decline-btn {
    width: 133px;
  }
}
</style>